import { useRef, useState, useCallback } from 'react';

export interface UseSubmitLockOptions {
    /** Optional callback invoked if an action is rejected because a lock is already active */
    onLockConflict?: () => void;
}

export interface UseSubmitLockReturn {
    /** Reactive state for UI disabled / loading bindings */
    isLocked: boolean;
    /** Synchronous ref for instant zero-latency checks */
    isLockedRef: React.MutableRefObject<boolean>;
    /** Executes the provided async or sync action with guaranteed single-flight lock */
    runWithLock: <T>(action: () => Promise<T> | T) => Promise<T | undefined>;
    /** Manually release the lock if needed */
    releaseLock: () => void;
}

/**
 * Custom hook to prevent duplicate submissions, double clicks, and race conditions
 * across UI buttons, modals, and network calls.
 *
 * Guarantees:
 * 1. Synchronous hardware-level locking via `useRef` (0ms window).
 * 2. Reactive state updating for UI disabling and spinners (`isLocked`).
 * 3. Guaranteed release in `finally` block preventing permanently locked buttons.
 */
export function useSubmitLock(options?: UseSubmitLockOptions): UseSubmitLockReturn {
    const isLockedRef = useRef(false);
    const [isLocked, setIsLocked] = useState(false);

    const releaseLock = useCallback(() => {
        isLockedRef.current = false;
        setIsLocked(false);
    }, []);

    const runWithLock = useCallback(async <T>(action: () => Promise<T> | T): Promise<T | undefined> => {
        if (isLockedRef.current) {
            options?.onLockConflict?.();
            return undefined;
        }

        isLockedRef.current = true;
        setIsLocked(true);

        try {
            return await action();
        } finally {
            isLockedRef.current = false;
            setIsLocked(false);
        }
    }, [options]);

    return {
        isLocked,
        isLockedRef,
        runWithLock,
        releaseLock
    };
}
